import type { ReactNode } from 'react'
import {
  ModalSheet,
  ModalSheetContent,
  ModalSheetFooter,
  ModalSheetFooterButtonGroup,
  ModalSheetFooterSpacer,
  ModalSheetHeader,
  ModalSheetSection,
  ModalSheetTitle,
} from '@/components/ModalSheet'
import { Form, FormGroup, Help, FormRow } from '@/components/Form'
import { Button } from '@/components/Button'

import styles from './ChargeOrRefundDialog.module.css'
import { ErrorBlock } from '@/components/ErrorBlock'
import {
  RadioButtonGroup,
  RadioButtonItem,
} from '@/components/RadioButtonGroup'
import type { ChargeOrRefundMode } from './ChargeOrRefundDialogViewModel'

export interface ChargeOrRefundDialogProps {
  showing: boolean
  onClose: () => void
  mode: ChargeOrRefundMode
  canCharge: boolean
  onChangeMode: (mode: ChargeOrRefundMode) => void
  billingMethodError?: string
  billingMethodSelect: ReactNode
  amountInput: ReactNode
  reasonInput: ReactNode
  submitError?: unknown
  onSubmitErrorClose: () => void
  onSubmit: () => void
  isSubmitPending: boolean
  isSubmitDisabled: boolean
}

export function ChargeOrRefundDialog({
  showing,
  onClose,
  mode,
  canCharge,
  onChangeMode,
  billingMethodError,
  billingMethodSelect,
  amountInput,
  reasonInput,
  submitError,
  onSubmitErrorClose,
  onSubmit,
  isSubmitPending,
  isSubmitDisabled,
}: ChargeOrRefundDialogProps) {
  return (
    <ModalSheet
      showing={showing}
      disableCloseOnEsc
      disableCloseOnOutsideClick
      onRequestClose={isSubmitPending ? undefined : onClose}
    >
      <ModalSheetHeader bordered withCloseButton>
        <ModalSheetTitle>Charge or refund this job</ModalSheetTitle>
      </ModalSheetHeader>

      <ModalSheetContent>
        <Form testId="charge-or-refund-dialog-form">
          <ModalSheetSection spaced>
            <FormRow>
              <FormGroup>
                <div className={styles.label}>What do you want to do?</div>
                <RadioButtonGroup size="big-wide">
                  <RadioButtonItem
                    onPress={() => onChangeMode('charge')}
                    active={mode === 'charge'}
                    isDisabled={!canCharge}
                  >
                    Charge
                  </RadioButtonItem>
                  <RadioButtonItem
                    onPress={() => onChangeMode('refund')}
                    testId="charge-or-refund-dialog-refund-button"
                    active={mode === 'refund'}
                  >
                    Refund
                  </RadioButtonItem>
                </RadioButtonGroup>
              </FormGroup>
            </FormRow>

            {mode === 'charge' && (
              <FormRow>
                <FormGroup label="Billing Method">
                  {billingMethodSelect}
                  <Help error={billingMethodError} />
                </FormGroup>
              </FormRow>
            )}

            <FormRow>{amountInput}</FormRow>

            <FormRow>{reasonInput}</FormRow>
          </ModalSheetSection>
        </Form>
      </ModalSheetContent>

      {!!submitError && (
        <ErrorBlock
          error={submitError}
          onClose={onSubmitErrorClose}
          tackOn="bottom"
        />
      )}

      <ModalSheetFooter>
        <ModalSheetFooterSpacer />
        <ModalSheetFooterButtonGroup>
          <Button
            label="Close"
            variant="default"
            isDisabled={isSubmitPending}
            onPress={onClose}
          />
          <Button
            label="Submit"
            variant="primary"
            onPress={onSubmit}
            isPending={isSubmitPending}
            isDisabled={isSubmitPending || isSubmitDisabled}
            testId="charge-or-refund-dialog-submit"
          />
        </ModalSheetFooterButtonGroup>
      </ModalSheetFooter>
    </ModalSheet>
  )
}
